import { AxiosResponse } from 'axios';
import {
	ConvertUserDTO,
	UpdateSelfDTO,
	User,
	UserDTO,
	UserUpdateDTO,
	UsersFilter,
} from '../data/models/user';
import { GoogleStorageDocument } from '../data/models/companyExternalUser';
import axiosClient from '../clients/axios-client';
import { stringifyReqObj } from '../utils/common-utils';

export async function createUsers(users: UserDTO[]): Promise<User[]> {
	const graphqlQuery = `
            mutation {
                createUsers(
                    data: ${stringifyReqObj(users)},
                ) {
                    ${userROGraphQl}
                }
            }
        `;

	const response: AxiosResponse = await axiosClient({
		url: 'graphql',
		method: `POST`,
		data: {
			query: graphqlQuery,
		},
	});
	return response?.data?.createUsers;
}

export async function fetchSelf(): Promise<User> {
	const graphqlQuery = `
            {
                fetchSelf {
                    ${userROWithProfileGraphQl}
                }
            }
        `;

	const response: AxiosResponse = await axiosClient({
		url: 'graphql',
		method: `POST`,
		data: {
			query: graphqlQuery,
		},
	});
	return response?.data?.fetchSelf;
}

export async function fetchSelfPermissions(): Promise<User> {
	const graphqlQuery = `
            {
                fetchSelfPermissions
            }
        `;

	const response: AxiosResponse = await axiosClient({
		url: 'graphql',
		method: `POST`,
		data: {
			query: graphqlQuery,
		},
	});
	return response?.data?.fetchSelfPermissions;
}

export async function updateUser(user: UserUpdateDTO): Promise<User> {
	const graphqlQuery = `
            mutation {
                updateUser(
                    data: ${stringifyReqObj(user)},
                ) {
                    ${userROGraphQl}
                }
            }
        `;

	const response: AxiosResponse = await axiosClient({
		url: 'graphql',
		method: `POST`,
		data: {
			query: graphqlQuery,
		},
	});
	return response?.data?.updateUser;
}

export async function updateSelf(user: UpdateSelfDTO): Promise<User> {
	const graphqlQuery = `
            mutation {
                updateSelf(
                    data: ${stringifyReqObj(user)},
                ) {
                    ${userROWithProfileGraphQl}
                }
            }
        `;

	const response: AxiosResponse = await axiosClient({
		url: 'graphql',
		method: `POST`,
		data: {
			query: graphqlQuery,
		},
	});
	return response?.data?.updateSelf;
}

export async function fetchUsers(
	filter: UsersFilter = {}
): Promise<{ list: User[]; count: number }> {
	const graphqlQuery = `
            {
                users(
                    filter: ${stringifyReqObj(filter)},
                ) {
                    list {
						${userROGraphQl}
					}
					count
                }
            }
        `;

	const response: AxiosResponse = await axiosClient({
		url: 'graphql',
		method: `POST`,
		data: {
			query: graphqlQuery,
		},
	});
	return response?.data?.users;
}

export async function getUser(id: string): Promise<User> {
	const graphqlQuery = `
            {
                user(
					id: "${id}",
                ) {
                    ${userROWithProfileGraphQl}
                }
            }
        `;

	const response: AxiosResponse = await axiosClient({
		url: 'graphql',
		method: `POST`,
		data: {
			query: graphqlQuery,
		},
	});
	return response?.data?.user;
}

export async function login(
	credential: string
): Promise<{ user: User; astroToken: string }> {
	const graphqlQuery = `
            mutation {
                login(
                    credential: "${credential}",
                ) {
                    astroToken
                    user {
                        ${userROGraphQl}
                    }
                }
            }
        `;

	const response: AxiosResponse = await axiosClient({
		url: 'graphql',
		method: `POST`,
		data: {
			query: graphqlQuery,
		},
	});
	return response?.data?.login;
}

export async function connectQuickBooksRedirect(): Promise<string> {
	const graphqlQuery = `
            mutation {
                connectQuickBooksRedirect
            }
        `;

	const response: AxiosResponse = await axiosClient({
		url: 'graphql',
		method: `POST`,
		data: {
			query: graphqlQuery,
		},
	});
	return response?.data?.connectQuickBooksRedirect;
}

export async function fetchMail(): Promise<any> {
	const graphqlQuery = `
            {
                mail
            }
        `;

	const response: AxiosResponse = await axiosClient({
		url: 'graphql',
		method: `POST`,
		data: {
			query: graphqlQuery,
		},
	});
	return response?.data?.mail;
}

export async function signup(
	credential: string
): Promise<{ user: User; astroToken: string }> {
	const graphqlQuery = `
        mutation {
            signup(
                credential: "${credential}",
            ) {
                astroToken
                user {
                    ${userROGraphQl}
                }
            }
        }
    `;

	const response: AxiosResponse = await axiosClient({
		url: 'graphql',
		method: 'POST',
		data: {
			query: graphqlQuery,
		},
	});
	return response?.data?.signup;
}

export async function updateProfilePicture(): Promise<string> {
	const graphqlQuery = `
            mutation {
                updateProfilePicture
            }
        `;

	const response: AxiosResponse = await axiosClient({
		url: 'graphql',
		method: `POST`,
		data: {
			query: graphqlQuery,
		},
	});
	return response?.data?.updateProfilePicture;
}

export async function convertExternalUser(user: ConvertUserDTO): Promise<User> {
	const graphqlQuery = `
		mutation {
			convertExternalUser(data: ${stringifyReqObj(user)}) {
				${userROGraphQl}
			}
		}
	`;

	const response: AxiosResponse = await axiosClient({
		url: 'graphql',
		method: 'POST',
		data: {
			query: graphqlQuery,
		},
	});
	return response?.data?.convertExternalUser;
}

export async function sendInvite(userId: string): Promise<boolean> {
	const graphqlQuery = `
		mutation {
			sendInvite(userId: "${userId}")
		}
	`;

	const response: AxiosResponse = await axiosClient({
		url: 'graphql',
		method: 'POST',
		data: {
			query: graphqlQuery,
		},
	});
	return response?.data?.sendInvite;
}

const userROGraphQl = `
    id
    email
    firstName
    lastName
	preferredName
	preferredPronouns
	personalEmail
	permissions
	role
	phone
	status
	alwaysAddToProjects
    acceptedInvite
	acceptedTermsAndConditions
    googleId
	googleProfilePictureUrl
	dateOfBirth
	preferences {
		theme
	}
	helpers {
		tourTooltipViewed
	}
	groups {
		id
		name
		description
		created
		updated
	}
	created
	updated
`;

export const userProfileROGraphQl = `
		companyId
		userId
		companyExternalUserId
		profilePictureUrl {
			uuid
			name
			url
			publicUrl
			mimeType
			size
			created
		}
		homeAddress {
			line1
			line2
			city
			state
			zip
			sameAsHomeAddress
		}
		shippingAddress {
			line1
			line2
			city
			state
			zip
			sameAsHomeAddress
		}
		driversLicenseNumber
		driversLicenseState
		noDriversLicense
		rate
		rateType
		dietaryRestrictions
		homeAirport
		passportNumber
		passportIssuingCountry
		passportExpirationDate
		noPassport
		globalEntry
		willingToTravelDomestically
		willingToTravelInternationally
		tsaPrecheck
		amtrackNumber
		deltaMileage
		unitedMileage
		preferredSeating
		starwoodMember
		emeraldClub
		preferredAirlines
		preferredHotels
		preferredCarRentals
		preferredRideSharingApps
		hiltonNumber
		marriottBonvoyNumber
		avisNumber
		budgetNumber
		certifications
		isIphone
		phoneCarrier
		smsTextReady
		canReceivePackagesAtHomeAddress
		emergencyContactFirstName
		emergencyContactLastName
		emergencyContactPhone
		websiteUrl
		instagramHandle
		resumeProjectHistoryUploads {
			uuid
			name
			url
			publicUrl
			mimeType
			size
			created
		}
		currentJobStatus
		unionStatus
		unionName
		productionRole
		yearsOfExperiencePrimary
		rateTenOrTwelveHours
		isRateFixed
		chargesOvertimeTypically
		workReferenceFirstName
		workReferenceLastName
		workReferenceEmail
		workReferencePhone
		workReference2FirstName
		workReference2LastName
		workReference2Email
		workReference2Phone
		secondaryProductionRole
		secondaryRate
		secondaryRateType
		secondaryYearsOfExperience
		secondaryRateTenOrTwelveHours
		secondaryIsRateFixed
		secondaryChargesOvertimeTypically
		secondaryWorkReferenceFirstName
		secondaryWorkReferenceLastName
		secondaryWorkReferenceEmail
		secondaryWorkReferencePhone
		secondaryWorkReference2FirstName
		secondaryWorkReference2LastName
		secondaryWorkReference2Email
		secondaryWorkReference2Phone
		hasKit
		hasVehicle
		willingToUseVehicleForProduction
		vehicleType
		vehicleLocation
		kitContents
		kitFee
		kitDocuments {
			uuid
			name
			url
			publicUrl
			mimeType
			size
			created
		}
		willingToBePhotographedOnSet
		shirtSize
		shoeSize
		pantSize
		localWorkLocations
		employmentTypePreference
		wantFreelanceContact
		interestedInW2Positions
		interestedInInternship
		loanOutCompanyName
		loanOutCompanyContactName
		loanOutCompanyPhone
		loanOutCompanyEmail
		hasRepresentation
		representativeFullName
		representativeCompany
		representativePhone
		representativeEmail
		billThroughAgentCompany
		preferredPaymentMethods
		hasWhatsapp
		whatsappNumber
		hasWechat
		wechatNumber
		healthInsurance
		professionalLicenses
		photographyConsent
		biopoc
`;

const userROWithProfileGraphQl = `
    ${userROGraphQl}
    userProfile {
        ${userProfileROGraphQl}
    }
`;
